/**
 * ReadingIndicator: the shared "reading" beat, front and center (WS-9 P0;
 * founder ask verbatim: "the status bars in the client tool that show
 * 'reading your note' should be front and center with a counter just like
 * the email and assessment tools otherwise they are missed").
 *
 * One component for three hosts: the composer's post-await beat, the
 * feed's retry / read-now re-read, and the two photo doors (the record's
 * Add from a photo and the Medication List's From a photo). Renders the
 * reading line in the drafters' treatment (X.3.5.1 elapsed-seconds
 * pattern; filled band, text-sm semibold, the house in-progress spinner).
 * The line names what is read: "Reading your note…", "Reading the
 * assessment…" or "Reading the photo…". The server reads an assessment,
 * and a case note long enough to need it, one section at a time and
 * reports n-of-N after each; the count joins the line when it arrives,
 * and the noun follows the KIND of the note being read, never the arrival
 * of the count. A photo is read in one pass, so its line never counts
 * sections; when several photos are read one after another (LP-45), the
 * line counts the photos instead ("Reading photo 2 of 5…"):
 *
 *   "Reading your note… 12s"
 *   "Reading your note… section 2 of 4 · 48s"
 *   "Reading the assessment… section 2 of 5 · 48s"
 *   "Reading the photo… 9s"
 *   "Reading photo 2 of 5… 31s"
 *
 * Before its first section reports, an assessment reads the plain note
 * line too.
 *
 * The counter is MOUNT-scoped, never request-scoped: it ticks from the
 * moment the host shows the beat until the host hides it, so it keeps
 * counting straight through the WS-9 auto-retry backoff sleeps (a
 * paused request must still read as alive — the quiet gap is exactly
 * what made teams conclude the tool died). The ticking seconds are
 * aria-hidden so a screen reader hears the reading line once (and each
 * section advance), not a per-second announcement.
 */

import { type ReactNode, useEffect, useState } from 'react';

/** What is being read: the two note kinds behind the composer's doors,
 * a photo on either photo door, or a PDF on the medication list's door. */
export type ReadingKind = 'note' | 'assessment' | 'photo' | 'pdf';

export interface ReadingProgress {
  done: number;
  total: number;
}

export interface ReadingIndicatorProps {
  /** What is being read; picks the noun once sections report. A photo
   * names itself from the start and never shows sections. */
  kind?: ReadingKind;
  /** Section progress, when the server reports it; joins the line. On a
   *  photo, which photo of how many is being read (`done` is the one being
   *  read); the line counts them only when there is more than one. */
  progress?: ReadingProgress | null;
  testId?: string;
}

export function ReadingIndicator({
  kind = 'note',
  progress = null,
  testId,
}: ReadingIndicatorProps): ReactNode {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const sections = kind === 'photo' || kind === 'pdf' ? null : progress;
  const line =
    kind === 'photo'
      ? progress !== null && progress.total > 1
        ? `Reading photo ${progress.done} of ${progress.total}…`
        : 'Reading the photo…'
      : kind === 'pdf'
        ? 'Reading the PDF…'
        : sections === null
        ? 'Reading your note…'
        : kind === 'assessment'
          ? `Reading the assessment… section ${sections.done} of ${sections.total}`
          : `Reading your note… section ${sections.done} of ${sections.total}`;
  return (
    <p
      role="status"
      data-testid={testId}
      className="flex min-h-11 items-center gap-2.5 rounded-base bg-sage-800 px-4 py-2 text-sm font-semibold text-cream shadow-sh1"
    >
      <span
        className="inline-block h-4 w-4 shrink-0 animate-spin rounded-pill border-2 border-current border-t-transparent"
        aria-hidden="true"
      />
      <span>
        {line}
        <span aria-hidden="true">{sections !== null ? ' · ' : ' '}{elapsed}s</span>
      </span>
    </p>
  );
}
